"use client";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, SearchIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import HighlightedText from "../custom/HighlightedText";
import { generateUrlSlug, GenericObjectInterface } from "@/src/utilities";
import {
  API_ENDPOINTS,
  BASE_URL,
  headersList,
} from "@/src/utilities/api/apiConfig";

type SearchVariant = "simple" | "hero";

interface UniversalSearchProps {
  variant?: SearchVariant;
  placeholder?: string;
  showButton?: boolean;
  className?: string;
  onSelectResult?: () => void;
  showDropdown?: boolean;
  defaultValue?: string;
}

// Fetch function for autosearch API
const fetchAutoSearch = async (searchQuery: string) => {
  if (!searchQuery || searchQuery.trim() === "") return [];
  const response = await fetch(
    `${BASE_URL}${API_ENDPOINTS.SEARCH_PRODUCTS}?query=${encodeURIComponent(
      searchQuery
    )}`,
    { headers: headersList }
  );
  if (!response.ok) throw new Error("Search failed");
  const result = await response.json();
  return result.data || [];
};

export function UniversalSearch({
  variant = "simple",
  placeholder = "Search products, articles, vendors...",
  showButton,
  className = "",
  onSelectResult,
  showDropdown = true,
  defaultValue = "",
}: UniversalSearchProps) {
  const router = useRouter();
  const [inputValue, setInputValue] = useState(defaultValue);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState({ name: "", id: "" });
  const searchRef = useRef<HTMLDivElement>(null);

  // Sync inputValue with defaultValue when it changes (e.g., from URL)
  useEffect(() => {
    setInputValue(defaultValue);
  }, [defaultValue]);

  // Automatically show button for hero variant if not explicitly set
  const shouldShowButton = showButton ?? variant === "hero";

  // Debounced setter for search query
  const debouncedSetQuery = useMemo(
    () => debounce((value: string) => setDebouncedQuery(value), 300),
    []
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetQuery.cancel();
    };
  }, [debouncedSetQuery]);

  // UseQuery for fetching search results
  const { data: searchResults = [], isLoading } = useQuery({
    queryKey: ["autosearch", debouncedQuery],
    queryFn: () => fetchAutoSearch(debouncedQuery),
    enabled: debouncedQuery.trim().length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Transform API results to component format
  const filteredItems = useMemo(() => {
    if (!searchResults || searchResults.length === 0) return [];

    return searchResults.map((item: GenericObjectInterface) => ({
      documentId: item.documentId,
      name: item?.name || item?.title || String(item),
      slug: item.slug,
      type: item.content_type,
      score: item.score,
      highlight: item.highlight,
      description: item.description,
      categoryName: item.category_name,
      logoUrl: item.product_logo_url,
    }));
  }, [searchResults]);

  // Handle click outside to close dropdown (only clear input if dropdown is shown)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        // Only clear input if dropdown is enabled (not on search results page)
        if (showDropdown) {
          setInputValue("");
          setDebouncedQuery("");
          setValue({ name: "", id: "" });
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    debouncedSetQuery(val);
    setValue({ name: val, id: "" });
    // Only open dropdown if showDropdown is enabled
    if (showDropdown) {
      setOpen(val.length > 0);
    }
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (inputValue.trim()) {
      setOpen(false);
      router.push(`/search?search=${encodeURIComponent(inputValue.trim())}`);
    }
  };

  const handleSelectChange = useCallback(
    (item: any) => {
      setValue({ name: item.name, id: item.documentId });
      setInputValue(item.name);
      setOpen(false);
      if (onSelectResult) {
        onSelectResult();
      }
      switch (item.type) {
        case "product":
          router.push(`/products/${item.slug}`);
          break;
        case "article":
          router.push(`/blog/${generateUrlSlug(item.name, item.documentId)}`);
          break;
        case "vendor":
          router.push(`/vendor-directory/${item.documentId}`);
          break;
        case "newsletter":
          router.push(
            `/newsletters/${generateUrlSlug(item.name, item.documentId)}`
          );
          break;
        default:
          router.push(
            `/search?search=${encodeURIComponent(item?.name?.trim())}`
          );

          break;
      }
    },
    [router]
  );

  const getTypeLabel = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Add spacing around highlighted <em> tags to prevent words from sticking together
  const formatHighlightedText = (html: string) => {
    return html
      .replace(/<em>/g, " <em>")
      .replace(/<\/em>/g, "</em> ")
      .replace(/\s+/g, " ")
      .trim();
  };

  // Variant-specific styles
  const containerStyles = {
    simple: "w-full mx-auto relative",
    hero: "w-full gap-4 p-1 bg-white/95 backdrop-blur rounded-2xl shadow-xl flex items-center justify-between border-primary border-2",
  };

  const searchIconStyles = {
    simple:
      "absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400",
    hero: "absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5",
  };

  const inputWrapperStyles = {
    simple: "relative",
    hero: "relative flex-1",
  };

  const inputStyles = {
    simple:
      "bg-white text-black w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-transparent",
    hero: "w-full pl-10 md:pl-12 md:pr-4 py-3 border-0 bg-transparent text-gray-800 placeholder:text-gray-500 text-sm md:text-base focus:outline-none focus:ring-0 focus:border-0 focus-visible:ring-0 !ring-0",
  };

  const dropdownStyles = {
    simple:
      "absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto",
    hero: "absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto",
  };

  const SearchContent = (
    <>
      <div className={inputWrapperStyles[variant]}>
        <Search className={searchIconStyles[variant]} />
        <Input
          type="text"
          value={inputValue}
          onChange={handleChange}
          placeholder={placeholder}
          className={inputStyles[variant]}
          onFocus={() => {
            if (showDropdown && inputValue.length > 0) {
              setOpen(true);
            }
          }}
          name="search-universal"
          autoComplete="off"
        />
      </div>

      {/* Dropdown Results */}
      {showDropdown && open && (
        <div className={dropdownStyles[variant]}>
          {isLoading ? (
            <div className="p-4 text-muted-foreground text-center text-sm">
              Searching...
            </div>
          ) : filteredItems.length === 0 && debouncedQuery.trim() !== "" ? (
            <div className="p-4 text-muted-foreground text-center text-sm">
              No results found for "{debouncedQuery}"
            </div>
          ) : (
            filteredItems?.map((item: GenericObjectInterface) => (
              <div
                key={`${item.type}-${item.documentId}`}
                onClick={() => handleSelectChange(item)}
                className="flex items-start gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                {item.logoUrl && (
                  <img
                    src={item.logoUrl}
                    alt={item.name}
                    className="w-10 h-10 object-contain rounded bg-gray-100 shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 text-sm">
                    <HighlightedText
                      text={item?.name ? item.name : item.title}
                      query={debouncedQuery}
                    />
                  </div>
                  {item.highlight?.description?.[0] ? (
                    <p
                      className="text-xs text-gray-500 line-clamp-2 mt-0.5"
                      dangerouslySetInnerHTML={{
                        __html: formatHighlightedText(
                          item.highlight.description[0]
                        ),
                      }}
                    />
                  ) : item.description ? (
                    <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                      {item.description}
                    </p>
                  ) : null}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-primary/65 font-medium">
                      {getTypeLabel(item.type)}
                    </span>
                    {item.categoryName && (
                      <>
                        <span className="text-xs text-gray-300">•</span>
                        <span className="text-xs text-gray-400">
                          {item.categoryName}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </>
  );

  return (
    <div ref={searchRef} className={`${containerStyles[variant]} ${className}`}>
      {variant === "simple" && !shouldShowButton ? (
        <form onSubmit={handleSearchSubmit} className="relative">
          {SearchContent}
        </form>
      ) : (
        <>
          {SearchContent}
          {shouldShowButton && (
            <Button
              size="icon"
              className="text-md font-semibold"
              onClick={handleSearchSubmit}
            >
              <SearchIcon />
            </Button>
          )}
        </>
      )}
    </div>
  );
}

export default UniversalSearch;
